import { useRef, useState, useEffect } from 'react';
import {
  Menu,
  MenuListItem,
  Input,
  IconArrowDropUp,
  IconArrowDropDown,
} from 'cdk-radial';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

export interface Option extends Record<string, unknown> {
  label: string;
  value?: string | never;
  disabled?: boolean;
}

const StyledInput = styled(Input)`
  input {
    caret-color: transparent;
  }
  svg {
    cursor: pointer;
  }
`;

export const StyledMenu = styled(Menu)`
  z-index: 1005 !important;
  width: 100%;
  max-width: 20% !important;
`;

export const MultiSelectDropdownInput = styled.div`
  margin-top:10px;
`;


export const MultiSelectDropdownMenu = (props: any) => {
  const { t } = useTranslation();
  const { options, onSelect, placeholder, label, name, value, isDisabled, isRequired, onError ,hasError ,maxSelection} = props;
  const targetRef = useRef();
  const dimensionRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(value || []);

  const isAnySelected = selectedOptions.length > 0;
  const isAllSelected =
    selectedOptions.length > 0 && selectedOptions.length === options.length;
  const isIndeterminate = !isAllSelected && isAnySelected;

  useEffect(() => {
    if(value){
      setSelectedOptions(value);
    }
  }, [value]);

  const handleSelect = (option: Option) => {
    const isSelected = selectedOptions.find((opt: any) => opt.label === option.label);
    // If already selected, allow deselect
    if (isSelected) {
      const selection = selectedOptions.filter((opt: any) => opt.label !== option.label);
      setSelectedOptions(selection);
      onSelect(selection);
      return;
    }
    // If not selected and already maxSelection reached, prevent further selection
    if (maxSelection && selectedOptions.length >= maxSelection) {
      // Optionally, show a warning here
      return;
    }
    const selection = [...selectedOptions, option];
    setSelectedOptions(selection);
    onSelect(selection);
  };

  const handleAllSelect = () => {

    if (!isAnySelected) {
      // Select only the first maxSelection non-disabled options
      let optionsExcludingDisabled = options.filter((option: any) => !option.isDisabled);
      if(maxSelection){
        optionsExcludingDisabled = optionsExcludingDisabled.slice(0, maxSelection);
      }
      setSelectedOptions(optionsExcludingDisabled);
      onSelect(optionsExcludingDisabled);
      return;
    }
    setSelectedOptions([]);
    onSelect([]);
  };

  const handleOpen = () => setIsOpen((isOpen) => !isOpen);

  const getIsOptionSelected = (option: Option) =>
    selectedOptions?.some((opt: any) => opt?.value === option?.value);

  const getValue = () => {
    if (isIndeterminate) {
      return selectedOptions.length === 1
        ? selectedOptions[0].label
        : t('multiSelect.selected', { count: selectedOptions.length });
    }
    if (isAllSelected) {
      return t('multiSelect.allSelected');
    }
    return '';
  };

  return (
    <div style={{width:'100%'}}>
      <MultiSelectDropdownInput>
        <StyledInput
          icon={isOpen ? <IconArrowDropDown /> : <IconArrowDropUp />}
          id={`${name}-id`}
          label={label}
          labelRef={dimensionRef}
          name={name}
          onError={onError}
          hasError={hasError}
          enableCustomValidation={true}
          onChange={() => {}} // onChange is required but in this case we don't need it
          placeholder={placeholder}
          ref={targetRef}
          value={getValue() || ''}
          isDisabled={isDisabled}
          isRequired={isRequired}
          title=""
        />
      </MultiSelectDropdownInput>
      {options?.length > 0 && (
        <StyledMenu
          dimensionRef={dimensionRef}
          isAuto={false}
          labelRef={targetRef}
          multi
          onClose={handleOpen}
          onOpen={handleOpen}
          style={{ zIndex: 1000 }}
          title=""
        >
          <MenuListItem
            dimensionRef={dimensionRef}
            onClick={handleAllSelect}
            isSelected={isAllSelected || isIndeterminate}
            indeterminate={isIndeterminate}
            showDivider
            hideSelectedItemCheckmark={false}
          >
            {t('multiSelect.selectAll')}
          </MenuListItem>
          {options?.map((option: any, index: any) => {
            const isSelected = getIsOptionSelected(option);
            // Disable unselected options if max is reached
            const disableOption = maxSelection && !isSelected && selectedOptions.length >= maxSelection;
            return (
              <MenuListItem
                dimensionRef={dimensionRef}
                key={`${option.label}-${index}-1`}
                isSelected={isSelected}
                isDisabled={disableOption || option.disabled}
                onClick={() => handleSelect(option)}
                hideSelectedItemCheckmark={false}
                title=""
              >
                {option.label}
              </MenuListItem>
            );
          })}
        </StyledMenu>
        )}
    </div>
  );
};
